import { motion, AnimatePresence } from "framer-motion";
import { B, PILLARS } from "../data/pillars";
import QuestionGuide from "./QuestionGuide";

export default function GuidedQuestion({
  flatQ, flatIdx, total, answers, confidenceMap,
  onAnswer, onConfidence, onBack, onNext, canNext,
}) {
  const { pillar, q, qi } = flatQ;
  const selected = answers[q.id];
  const confidence = confidenceMap[q.id];
  const pct = Math.round(((flatIdx) / total) * 100);

  return (
    <div style={{
      minHeight: "100vh", background: B.navy,
      display: "flex", flexDirection: "column",
      fontFamily: "'Segoe UI',system-ui,sans-serif",
    }}>
      {/* Progress bar */}
      <div style={{ height: 4, background: "#1E3A5F", flexShrink: 0 }}>
        <motion.div
          animate={{ width: pct + "%" }}
          transition={{ duration: 0.4 }}
          style={{ height: "100%", background: pillar.color }}
        />
      </div>

      {/* Top bar */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 28px", borderBottom: "1px solid #1E3A5F",
        flexShrink: 0, flexWrap: "wrap", gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>{pillar.icon}</span>
          <span style={{ color: pillar.color, fontSize: 13, fontWeight: 700 }}>{pillar.label}</span>
          <span style={{ color: B.subText, fontSize: 12 }}>· {pillar.sub}</span>
        </div>
        <div style={{ color: B.subText, fontSize: 12, fontWeight: 600 }}>
          Question {flatIdx + 1} of {total}
        </div>
        <div style={{ fontSize: 12, color: B.subText }}>{pct}% complete</div>
      </div>

      {/* Question area */}
      <div style={{
        flex: 1, display: "flex", alignItems: "flex-start",
        justifyContent: "center", padding: "32px 24px", overflowY: "auto",
      }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={q.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            style={{ maxWidth: 700, width: "100%" }}
          >
            <div style={{
              marginBottom: 6, fontSize: 11, color: B.subText,
              fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
            }}>
              {pillar.label} · {qi + 1} of {pillar.questions.length}
            </div>

            <h2 style={{
              fontSize: "clamp(20px,4vw,30px)", fontWeight: 900,
              color: B.white, marginBottom: 20, lineHeight: 1.2,
            }}>
              {q.label}
            </h2>

            {/* Guidance panel */}
            <QuestionGuide qid={q.id} pillarColor={pillar.color} />

            {/* Options */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
              {q.opts.map((opt, oi) => {
                const isSelected = selected === oi;
                const levelColors = [B.red, B.orange, B.yellow, B.green, B.teal];
                const lc = levelColors[oi];
                return (
                  <motion.button
                    key={oi}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => onAnswer(q.id, oi)}
                    style={{
                      background: isSelected ? pillar.color + "20" : "#122240",
                      border: `2px solid ${isSelected ? pillar.color : "#1E3A5F"}`,
                      borderRadius: 12,
                      padding: "14px 20px",
                      color: isSelected ? B.white : B.lightGray,
                      fontSize: 14,
                      cursor: "pointer",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      transition: "all 0.15s",
                    }}
                  >
                    <div style={{
                      width: 30, height: 30, borderRadius: "50%",
                      border: `2px solid ${isSelected ? pillar.color : lc + "60"}`,
                      background: isSelected ? pillar.color : lc + "15",
                      flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, color: isSelected ? B.white : lc, fontWeight: 800,
                    }}>
                      {isSelected ? "✓" : oi + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, color: lc,
                        marginRight: 10, letterSpacing: 1,
                        background: lc + "15", padding: "2px 7px",
                        borderRadius: 4, display: "inline-block", marginBottom: 3,
                      }}>
                        OML {oi + 1}
                      </span>
                      <div style={{ fontSize: 14, color: isSelected ? B.white : B.lightGray }}>
                        {opt}
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Confidence toggle */}
            {selected !== undefined && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  background: "#122240", borderRadius: 12,
                  padding: "16px 20px", border: "1px solid #1E3A5F",
                }}
              >
                <div style={{
                  fontSize: 12, color: B.subText, fontWeight: 700,
                  letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12,
                }}>
                  How confident are you in this answer?
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  {[
                    { val: true, label: "✓ Confident", color: B.green },
                    { val: false, label: "⚠ Unsure / Estimate", color: B.yellow },
                  ].map(opt => (
                    <button
                      key={String(opt.val)}
                      onClick={() => onConfidence(q.id, opt.val)}
                      style={{
                        flex: 1,
                        background: confidence === opt.val ? opt.color + "20" : "#0D1B2E",
                        border: `1px solid ${confidence === opt.val ? opt.color : "#1E3A5F"}`,
                        borderRadius: 8, padding: "10px 14px",
                        color: confidence === opt.val ? opt.color : B.subText,
                        fontSize: 13, fontWeight: 700, cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Nav */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        padding: "20px 28px", borderTop: "1px solid #1E3A5F",
        flexShrink: 0, gap: 12,
      }}>
        <button
          onClick={onBack}
          disabled={flatIdx === 0}
          style={{
            background: flatIdx === 0 ? "#1E3A5F" : "#122240",
            border: "1px solid #1E3A5F", borderRadius: 10,
            padding: "12px 24px",
            color: flatIdx === 0 ? "#3A5A7A" : B.lightGray,
            fontSize: 14, cursor: flatIdx === 0 ? "not-allowed" : "pointer", fontWeight: 600,
          }}
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!canNext}
          style={{
            background: canNext
              ? `linear-gradient(135deg,${pillar.color},${pillar.color}CC)`
              : "#1E3A5F",
            border: "none", borderRadius: 10, padding: "12px 32px",
            color: B.white, fontSize: 14, fontWeight: 800,
            cursor: canNext ? "pointer" : "not-allowed",
          }}
        >
          {flatIdx === total - 1 ? "Generate Report →" : "Next →"}
        </button>
      </div>
    </div>
  );
}